import {createStore} from 'vuex'
import {setAppVersionWatcherEnabled} from '@/service/AppVersionWatcher';
import createPersistedState from "vuex-persistedstate";
import AccessTokenDTO from "@/models/AccessTokenDTO";
import TraderDTO from "@/models/TraderDTO";
import router from './../router'
import APIClient from "@/service/APIClient";
import currencyMethods from "@/store/currencyMethods";
import {UserRole} from "@/models/enum/UserRole";
import {UserPermission} from "@/models/enum/UserPermission";
import MerchantDTO from "@/models/MerchantDTO";
import UserDTO from "@/models/UserDTO";
import devices from "@/store/devices";
import merchantStores from "@/store/merchantStores";
import reqGroups from "@/store/reqGroups";
import rateSources from "@/store/rateSources";
import AppDataDTO from "@/models/AppDataDTO";
import {Direction} from "@/models/enum/Direction";
import FrontendAppDTO from "@/models/FrontendAppDTO";
import MerchantOperatorDTO from "@/models/MerchantOperatorDTO";
import trafficGroups from "@/store/trafficGroups";
import exportTasks from "@/store/exportTasks";
import {helper} from "@/service/CommonHelper";
import TraderOperatorDTO from "@/models/TraderOperatorDTO";

function loadFromLocalStorage(key: string, def: any): any {
  try {
    const data = localStorage.getItem(key) || def;
    return JSON.parse(data) as any;
  } catch (e) {
    return null
  }
}

function loadTokenFromLocalStorage(): AccessTokenDTO | null
{
  return loadFromLocalStorage('auth', '') as AccessTokenDTO
}

function loadAppFromLocalStorage(): AppDataDTO | null
{
  return loadFromLocalStorage('app', '') as AppDataDTO
}

function resolveTheme(): string|null
{
  let theme = loadFromLocalStorage('theme', null) as string|null
  if (['light', 'dark'].includes(theme ?? '')) {
    return theme
  }

  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    theme = 'dark'
  } else {
    theme = 'light'
  }

  return theme
}

const auth = loadTokenFromLocalStorage();
const appData = loadAppFromLocalStorage();

const api = new APIClient()
export default createStore({
  state: {
    theme: resolveTheme() as string,
    apps: [] as FrontendAppDTO[],
    lang: loadFromLocalStorage('lang', null) as string|null,
    auth: auth as AccessTokenDTO | null,
    user: null as TraderDTO | MerchantDTO | UserDTO | MerchantOperatorDTO | TraderOperatorDTO | null,
    appData: appData as AppDataDTO | null,
    afLastId: loadFromLocalStorage('afLastId', null) as number|null,
    afHasNew: false as boolean,
    toggleDealsIsAutoReload: false as boolean,
    accountCurrencies: [] as string[],
  },
  mutations: {
    setApps(state, apps: FrontendAppDTO[]) {
      state.apps = apps
    },
    setAccountCurrencies(state, currencies: string[]) {
      state.accountCurrencies = currencies
    },
    setAfLastId(state, lastId: number|null) {
      state.afLastId = Math.max(lastId ?? 0, state.afLastId ?? 0)
      localStorage.setItem('afLastId', JSON.stringify(state.afLastId));
    },
    setTheme(state, theme: string) {
      state.theme = theme
      localStorage.setItem('theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
      document.querySelector('meta[name=theme-color]')?.setAttribute('content', state.theme === 'light' ? '#eceff3' : '#1c1c1c');
    },
    initTheme(state) {
      document.documentElement.setAttribute('data-theme', state.theme);
      document.querySelector('meta[name=theme-color]')?.setAttribute('content', state.theme === 'light' ? '#eceff3' : '#1c1c1c');
    },
    toggleTheme(state) {
      state.theme = state.theme === 'light' ? 'dark' : 'light'
      localStorage.setItem('theme', state.theme);
      document.documentElement.setAttribute('data-theme', state.theme);
      document.querySelector('meta[name=theme-color]')?.setAttribute('content', state.theme === 'light' ? '#eceff3' : '#1c1c1c');
    },
    setAfHasNew(state, hasNew: boolean) {
      state.afHasNew = hasNew
    },
    setAppData(state, appData: AppDataDTO) {
      state.appData = appData;
      setAppVersionWatcherEnabled(appData.FRONTEND_AUTO_UPDATE_ENABLED === true);
      localStorage.setItem('app', JSON.stringify({version: appData.version, ga_id: appData.ga_id}));
    },
    authenticate(state, token: AccessTokenDTO) {
      state.auth = token;
      localStorage.setItem('auth', JSON.stringify({token: token.token, refreshToken: token.refreshToken}));
    },
    removeAuth(state) {
      localStorage.removeItem('auth');
      state.auth = null;
      state.user = null;
    },
    setUser(state, user: TraderDTO | null) {
      state.user = user;
      window.userTimeZone = user?.timezone ?? 'UTC';
      helper.setLang(user?.lang)
      state.lang = window.userLang
      localStorage.setItem('lang', state.lang ?? '');
    },
    setUserStatus(state, status: any) {
      let trader = state.user as TraderDTO
      if (state.user?.role === UserRole.TRADER_OPERATOR) {
        trader = (state.user as TraderOperatorDTO)!.trader as TraderDTO
      }

      if (status.direction === Direction.PAY_IN) {
        trader!.isActive = status.isActive
      } else {
        trader!.isPayOutActive = status.isActive
      }
    },
    setToggleDealsIsAutoReload(state, hasNew: boolean) {
      state.toggleDealsIsAutoReload = hasNew
    },
  },
  actions: {
    async loadApps({ state, commit }) {
      const apps = await  api.appsAll()
      commit('setApps', apps);
    },
    async loadAppData({commit}) {
      try {
        const appData = await api.app()
        commit('setAppData', appData);
      } catch (e) {

      }
    },
    async loadUser({commit}) {
      try {
        const user = await api.me()
        commit('setUser', user);
      } catch (e) {

      }
    },
    afLastId({commit}, lastId: number|null) {
      commit('setAfLastId', lastId);
    },
    afHasNew({commit}, hasNew: boolean) {
      commit('setAfHasNew', hasNew);
    },
    logout({commit}) {
      setAppVersionWatcherEnabled(false);
      commit('removeAuth');
    },
    toggleTraderStatus({commit}, status: any) {
      return api.changeMeStatus(status.isActive, status.direction).then(() => {
        commit('setUserStatus', status);
      }).catch(() => {

      });
    },
    login({commit}, credentials) {

      return api.tokenGet(credentials).then((accessToken) => {
        commit('authenticate', accessToken);
        commit('merchantStores/SET_CURRENT_STORE', null);
      }).catch((error) => {
        throw error;
      });
    },
    useAccessToken({commit}, accessToken: AccessTokenDTO) {
      commit('authenticate', accessToken);
      commit('merchantStores/SET_CURRENT_STORE', null);
    },
    setAppData({commit}, appData:AppDataDTO) {
      commit('setAppData', appData);
    },
    setAccountCurrencies({commit}, currencies:AppDataDTO) {
      commit('setAccountCurrencies', currencies);
    },
    toggleDealsIsAutoReload({commit}, isAutoReload: boolean) {
      commit('setToggleDealsIsAutoReload', isAutoReload);
    }
  },
  getters: {
    getAccountCurrencies: state => state.accountCurrencies,
    getApps: state => state.apps,
    getAppsIds: state => state.apps.map(item => item.id),
    getInviteLink: state => (user: UserDTO): string => {
      const app = state.apps.find(item => item.id === user.appId)
      if (!app) {
        return `${document.location.origin}/invite/${user.inviteToken}`;
      }

      if (app.frontendAppDomain && ([UserRole.TRADER, UserRole.TRADER_OPERATOR] as string[]).includes(user.role)) {
        return `${document.location.protocol}//${app.frontendAppDomain}/invite/${user.inviteToken}`;
      }

      return `${document.location.protocol}//app.${app.domain}/invite/${user.inviteToken}`;
    },
    getAppById: state => (id: number|null): FrontendAppDTO|null|undefined => {
      if (!id) {
        return null
      }
      return state.apps.find(item => item.id === id)
    },
    getAfLastId: state => state.afLastId,
    getAfHasNew: state => state.afHasNew,
    lang: state => state.lang,
    isLoggedIn: state => state.auth !== null,
    getAppData: state => state.appData,
    getAuth: state => state.auth,
    getUser: state => state.user,
    getRole: state => state.user?.role,
    hasRole: state => (roles: string[]): boolean => {
      if (!state.user?.role) {
        return false;
      }
      return roles.includes(state.user!.role)
    },
    hasPermission: state => (permission: string): boolean => {
      if (!state.user?.permissions) {
        return false;
      }

      return state.user.permissions.includes(permission)
    },
    isProvider: state => state.user && state.user.role === UserRole.TRADER && (state.user as TraderDTO).pcId,
    isTrader: state => state.user && state.user.role === UserRole.TRADER,
    isTraderOperator: state => state.user && state.user.role === UserRole.TRADER_OPERATOR,
    isTeamLead: state => state.user && state.user.role === UserRole.TRADER_TEAM_LEAD,
    isOperator: state => state.user && state.user.role === UserRole.OPERATOR,
    isAdmin: state => state.user && state.user.role === UserRole.ADMIN,
    isMerchant: state => state.user && state.user.role === UserRole.MERCHANT,
    isMerchantOperator: state => state.user && state.user.role === UserRole.MERCHANT_OPERATOR,
    isReferral: state => state.user && state.user.role === UserRole.REFERRAL,
    toggleDealsIsAutoReload: state => state.toggleDealsIsAutoReload
  },
  strict: true,
  plugins: [createPersistedState()],
  modules: {
    currencyMethods,
    devices,
    merchantStores,
    reqGroups,
    rateSources,
    trafficGroups,
    exportTasks
  }
})
